import { IArticleFields, IArticleTextBlock, ILinkBlock, getRelatedContent, isArticle } from '@estercare/ester-shared'
import {
  useGetArticleBySlug,
  useGetLocalisedArticleBySwedishSlug,
  useGetEntriesByContentType,
  useGetContentWithSwedishFallback,
} from '../../../api/contentful/contentfulQueries'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { Title3 } from '../../../components/styles/Typography'
import styled from 'styled-components'
import { theme } from '../../../theme/theme'
import { useParams } from 'react-router-dom'
import { RichText } from '../../../components/richText/RichText'
import { Header } from '../../../components/Header'
import { PreviewGuard } from '../../../components/PreviewGuard'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import { Author } from '../../../components/Author'
import { ListenToPodcastButton } from '../../../components/ListenToPodcastButton'
import { ResultList } from '../../../components/ResultList'
import { useGetQueryParams } from '../../../hooks/useGetQueryParams'
import { SessionStorageKey, useSessionStorage } from '../../../hooks/useSessionStorage'
import { useEffect } from 'react'
import { KnowledgebankFilterProps } from './Index'
import { useTranslation } from 'react-i18next'

type ArticleContent = IArticleTextBlock | ILinkBlock

const hasTypeTextBlock = (item: ArticleContent): item is IArticleTextBlock => {
  return Object.prototype.hasOwnProperty.call(item.fields, 'text')
}

const hasTypeLink = (item: ArticleContent): item is ILinkBlock => {
  return Object.prototype.hasOwnProperty.call(item.fields, 'url')
}

export const Article = () => {
  const { t } = useTranslation()
  const [, setKnowledgebankFilter] = useSessionStorage<KnowledgebankFilterProps>(SessionStorageKey.KNOWLEDGEBANK_FILTER)
  const slug = useParams()
  const queryParams = useGetQueryParams(['y', 'article'])
  const articleQuery = useGetArticleBySlug(slug?.id || '')

  //This is a temporary fallback solution until we have the new healthplan without Swedish slugs
  const { data: article, isLoading: articleIsLoading } = useGetContentWithSwedishFallback<IArticleFields>(
    articleQuery,
    useGetLocalisedArticleBySwedishSlug,
    slug?.id || ''
  )

  useEffect(() => {
    if (queryParams.y && queryParams.article) {
      setKnowledgebankFilter({
        savedArticleIndex: Number(queryParams.article),
        savedScrollPosition: Number(queryParams.y),
        browserIndex: window.history.length,
      })
    }
  }, [queryParams.article, queryParams.y, setKnowledgebankFilter])

  const allArticles = useGetEntriesByContentType<IArticleFields>('article').data?.items

  if (articleIsLoading) return <LoadingSpinner />
  if (!isArticle(article)) return <PreviewGuard />

  const { abstract, articleContent, primaryImage, title, medicalReviewer } = article.fields

  const allArticlesNoMini = allArticles && allArticles.filter((article) => !article.fields.isMiniArticle)
  const relatedArticles = allArticlesNoMini
    ? getRelatedContent(article, allArticlesNoMini, 4)?.map((entry) => entry.fields)
    : null

  return (
    isArticle(article) &&
    title && (
      <>
        <Header title={title} description={abstract} image={primaryImage} backButtonLink="/portal/resource-center" />
        <ComponentLayout>
          <ContentContainer>
            {Array.isArray(articleContent) &&
              articleContent?.map((item: ArticleContent, index) => {
                if (hasTypeTextBlock(item) && item.fields.text) {
                  return (
                    <ItemContainer key={index}>
                      <RichText richText={item.fields.text} />
                    </ItemContainer>
                  )
                }
                if (hasTypeLink(item)) {
                  const { label, linkText, url } = item.fields
                  if (!label || !linkText || !url) return null
                  return (
                    <PodcastContainer key={index}>
                      <ListenToPodcastButton key={index} subtitle={linkText} href={url} />
                    </PodcastContainer>
                  )
                }
                return null
              })}
            <ItemContainer> {medicalReviewer && <Author {...medicalReviewer.fields} />}</ItemContainer>
          </ContentContainer>
        </ComponentLayout>
        {relatedArticles && (
          <ComponentLayout noMaxWidth backgroundColor={theme.color.beigeDark}>
            <StyledTitle3>{t('employee.knowledgeBank.relatedArticles')}</StyledTitle3>
            <ResultList title="" items={relatedArticles} />
          </ComponentLayout>
        )}
      </>
    )
  )
}

const ItemContainer = styled.div`
  width: 100%;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    padding-left: 15%;
    padding-right: 15%;
  }
`

const PodcastContainer = styled.div`
  padding-top: ${theme.spacing.medium}px;
  padding-bottom: ${theme.spacing.large}px;
`

const StyledTitle3 = styled(Title3)`
  text-align: center;
  margin-bottom: ${theme.spacing.mediumLarge}px;
`
